import React, { useReducer, useRef, useState, useEffect } from "react";
import { Product, ProductPageReview } from "../../utils/types";
import SelectReviewOrder, { ReviewOrderActionsType } from "./selectReviewOrder";
import Stars from "../shared/stars";
import PrimaryButton from "../shared/primaryButton";
import ProductReviewCard from "./productReviewCard";
import ReviewStars from "../shared/reviewStars";
import { submitReview } from "../../utils/api";
import { __t } from "../../utils/translate";
import { gtag } from "../../utils/utils";

type CustomerReviewsProps = {
    className?: string;
    data: ProductPageReview[];
    product: Product;
};

const CustomerReviews: React.FC<CustomerReviewsProps> = ({
    className = "",
    reviews,
    product,
}) => {
    // prettier-ignore
    const reviewsOrderReducer = (reviews: ProductPageReview[], action: ReviewOrderActionsType) => {
        switch (action) {
            case "newest":
                return [
                    ...reviews.sort(
                        (a, b) =>
                            new Date(b.date_created).getTime() -
                            new Date(a.date_created).getTime()
                    ),
                ];
            case "oldest":
                return [
                    ...reviews.sort(
                        (a, b) =>
                            new Date(a.date_created).getTime() -
                            new Date(b.date_created).getTime()
                    ),
                ];
            case "highest_rated":
                return [...reviews.sort((a, b) => b.rating - a.rating)];
            case "lowest_rated":
                return [...reviews.sort((a, b) => a.rating - b.rating)];
            default:
                return reviews;
        }
    };
    const [orderedReviews, dispatch] = useReducer(reviewsOrderReducer, reviews);

    const reviews_step = 6; //6
    const [visibleReviews, setVisibleReviews] = useState(reviews_step);

    const showMoreReviews = () => {
        gtag("event", "click_reviews_pagination");

        setVisibleReviews((prev) => prev + reviews_step);
    };

    const [isActive, setIsActive] = useState(false);

    const handleClick = () => {
        setIsActive(!isActive);
    };

    return (
        <div className={`${className}`} >
            {/* <h2 className="text-center text-h2-r md:text-h2 text-main-text-color pb-8 sm:pb-10 xl:pb-14">
                {__t("Rating and reviews")}
            </h2> */}
            <div className="mb-6 md:mb-10 flex flex-col md:flex-row items-center justify-start">
                <div className="flex flex-col w-full font-reviews md:w-1/3 md:py-5 md:border-r md:border-border-color">
                    <div className="px-0 md:px-4 flex flex-col items-start justify-start md:justify-center md:items-center w-full md:min-w-[240px] xl:min-w-[270px]">
                        <div className="flex flex-row md:flex-col-reverse items-center justify-center gap-[6px] md:gap-3">
                            <Stars
                                width="140"
                                height="28"
                                rating={Number(product.average_rating)}
                                useNewDesign={true}
                            />
                            <span className="text-small-14 px-2 py-[7px] rounded-sm border border-border-color md:border-none md:text-h1-sm md:font-normal text-[#4A4A4A]">{`${product.average_rating}`}</span>
                        </div>
                        <div className="text-small-14 font-bold text-[#666] my-[10px] flex justify-center">{`${product.rating_count} ${__t("Reviews")}`}</div>
                    </div>
                    <WriteReview
                        className="w-full"
                        productId={product.wordpress_id}
                        handleClick={handleClick}
                        isActive={isActive}
                        onlyButton={false}
                    />
                </div>

                <div className="flex flex-row md:flex-col w-full md:w-1/3 gap-[10px] items-center md:h-full font-reviews">
                    <div className="bg-[#0a8900] flex flex-row items-center rounded-[6px] md:rounded-none gap-[5px] lg:pb-1 lg:pr-[5px]">
                        <svg className="m-[6px] md:m-2 w-[15px] h-[15px] md:w-[30px] md:h-[30px]" viewBox="0 0 176 176" focusable="false" aria-hidden="true">
                            <circle fill="#0A8900" stroke="white" strokeWidth="8" cx="88" cy="88" r="81.2"></circle>
                            <path fill="white" stroke="white" strokeWidth="5" d="M77.2 121.3L43 87.2l5.5-5.6 28.7 28.6 50.3-50.3 5.5 5.6"></path>
                        </svg>
                        <span className="text-base-16 md:text-[40px] leading-7 md:leading-[56px] text-white mr-[6px]">95%</span>
                    </div>
                    <p className="text-small-14 text-[#666] !text-[10px] md:max-w-[145px] leading-3 md:text-center">{__t("of respondents would recommend this to a friend")}</p>
                </div>

                <RatingBar reviews={reviews} className="w-full md:w-1/3 md:py-10 lg:pr-14 border-t-[1px] md:border-t-0 md:border-l-[1px] md:p-5 border-border-color pt-4 mt-4 md:mt-0" />
            </div>

            {reviews?.length ? (
                <div className="relative mb-4 z-20">
                    <SelectReviewOrder onSelect={dispatch} />
                </div>
            ) : null}

            <div
                className={`${!isActive ? "max-h-0" : "max-h-[1000px]"}
                    overflow-hidden transition-all duration-300`}
            >
                <div className={`pt-10 pb-8 px-2`}>
                    <WriteReviewForm productId={product.wordpress_id} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[28px]" >
                {orderedReviews.slice(0, visibleReviews).map((review) => (
                    <ProductReviewCard
                        key={review.id}
                        className={`w-full`}
                        userName={review.reviewer}
                        description={review.review}
                        rating={review.rating}
                        date={review.date_created}
                        isVerified={review.status}
                    />
                ))}
            </div>
            {visibleReviews < orderedReviews.length && (
                <button
                    className="w-full text-center text-base-16 underline text-body-text-color mt-[32px] sm:mt-[40px] lg:mt-[60px]"
                    onClick={showMoreReviews}
                >
                    {__t("Show more")}
                </button>
            )}
        </div>
    );
};

export default CustomerReviews;

type WriteReviewFormProps = {
    productId: number;
};

const WriteReviewForm: React.FC<WriteReviewFormProps> = ({ productId }) => {
    const [rating, setRating] = useState(0);
    const [message, setMessage] = useState<null | string>(null);

    const formRef = useRef<HTMLFormElement | null>(null);
    // React.MutableRefObject;

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const target = event.currentTarget;

        const data = {
            product_id: productId,
            review: target["review-textarea"].value,
            reviewer: target["review-name"].value,
            reviewer_email: target["review-email"].value,
            rating,
        };

        const res = await submitReview(data);

        if (res?.id) {
            setMessage(__t("Your review has been successfully submitted!"));
            formRef?.current && formRef.current.reset();
        }
    };

    return (
        <div className="text-base-16 font-reviews">
            <p className="mb-6 text-title text-[#111]">{__t("Add a review")}</p>
            <form ref={formRef} className="" onSubmit={handleSubmit}>
                <div className="flex flex-col md:flex-row md:gap-2 text-base-16 lg:text-h3-r">
                    <p className="mb-1">{__t("Your email address will not be published.")}</p>
                    <p className="mb-6 md:mb-6">
                        {__t("Required fields are marked")} <span className="text-red">*</span>
                    </p>
                </div>
                <div className="mb-[24px] flex flex-col gap-[16px] text-[#111]">
                    <span>{__t("Your rating")} <span className="text-red">*</span></span>
                    {/* TODO: implement selecting stars */}
                    <ReviewStars
                        width="85"
                        height="16"
                        setRating={(v: number) => setRating(v)}
                        rating={rating}
                        starColor="#ee9e13"
                    />
                </div>
                <label htmlFor="review-textarea" className="text-[#111]">
                    {__t("Your review")} <span className="text-red">*</span>
                </label>
                <textarea
                    className="w-full border border-border-color active:border-dark-blu my-5"
                    name="review-textarea"
                    id="review-textarea"
                    cols={45}
                    rows={6}
                    minLength={5}
                    required
                ></textarea>
                <div className="flex flex-col lg:flex-row w-full gap-5 text-[#111]">
                    <div className="mb-[6px] flex flex-col lg:w-full">
                        <label className="mb-4" htmlFor="review-name">
                            {__t("Name")} <span className="text-red">*</span>
                        </label>
                        <input
                            className="border border-border-color active:border-dark-blu p-[8px]"
                            id="review-name"
                            type="text"
                            required
                        />
                    </div>
                    <div className="mb-[24px] flex flex-col lg:w-full">
                        <label className="mb-4" htmlFor="review-email">
                            {__t("Email")} <span className="text-red">*</span>
                        </label>
                        <input
                            className="border border-border-color active:border-dark-blu p-[8px]"
                            id="review-email"
                            type="email"
                            required
                        />
                    </div>
                </div>
                <PrimaryButton className="px-6 py-2 rounded-[3px]" textStyle="!text-base-16" type="submit">
                    {__t("Submit")}
                </PrimaryButton>
                {!!message && (
                    <p className="text-base mt-[10px] text-dark-gray">{message}</p>
                )}
            </form>
        </div>
    );
};

type WriteReviewProps = {
    className?: string;
    productId: number;
    handleClick: () => void;
    isActive: boolean;
    onlyButton: boolean;
};

const WriteReview: React.FC<WriteReviewProps> = ({ productId, className, handleClick, isActive, onlyButton }) => {

    return (
        <div
            className={`${className} items-center justify-between overflow-hidden`}
        >
            <div
                onClick={handleClick}
                className={`flex cursor-pointer items-start justify-between pb-4 md:pb-0`}
            >
                <button
                    type="button"
                    className={`group overflow-hidden transition-all duration-300 
                        disabled:bg-stroke-color group w-full md:w-fit md:mx-auto border-b-2 border-blue rounded-sm bg-[#0b7bc1] enabled:hover:bg-[#c0e5fb] enabled:hover:border-[#82caf8]`}
                >
                    <div className="flex items-center justify-center px-4 py-3">
                        <span
                            className={`whitespace-nowrap text-small-14 leading-[14px] font-bold !text-white group-hover:!text-[#085c91] fill-body-text-color group-hover:fill-white focus:text-white`}
                        >
                            {__t("Write a review")}
                        </span>
                    </div>
                </button>
            </div>
        </div>
    );
};

type RatingBarProps = {
    className?: string;
    reviews: ProductPageReview[];
};

const RatingBar: React.FC<RatingBarProps> = ({ className, reviews }) => {
    const arr = Array.from({ length: 5 }, (_, i) => ({
        rating: i + 1,
        count: 0,
        percentage: 0,
    }));

    // count number of reviews with each rating
    reviews.forEach((r) => {
        if (arr[r.rating - 1]) {
            arr[r.rating - 1].count += 1;
            arr[r.rating - 1].percentage =
                Math.round((arr[r.rating - 1].count / reviews.length) * 100);
        }
    });

    return (
        <div className={className}>
            {arr.reverse().map((item, index) => (
                <div
                    key={item.rating}
                    className="grid gap-0 md:gap-1 mb-[4px] w-full grid-cols-[min-content_1fr_min-content]">
                    <p className="text-base-16 lg:text-h3-r px-3 whitespace-nowrap w-20 text-[#0b7bc1]" aria-hidden="true">
                        {5 - index} {5 - index > 1 ? __t('Stars') : __t('Star')}
                    </p>
                    <div className="h-[27px] bg-[#f6f6f6] rounded-sm w-full">
                        <div
                            className="h-full bg-[#f7941d] rounded-sm"
                            style={{ width: `${item.percentage}%` }}
                        ></div>
                    </div>
                    <div className="text-base-16 lg:text-title text-end text-[#5d5d5d] !font-bold pl-3 pr-10 lg:pr-0 w-10 flex-auto">
                        {item.count}
                    </div>
                </div>
            ))}
        </div>
    );
};
